/* PraticaArvalPlaceholderPage.jsx — Placeholder modulo pratica NLT Arval.
 * In attesa della documentazione/integrazione ufficiale con Arval Italia
 * mostriamo un messaggio informativo + CTA "Richiedi info" al backoffice.
 */
import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { CarFront, Mail, Phone, MapPin, ArrowLeft, Construction } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PraticaArvalPlaceholderPage() {
  const { carId } = useParams();
  const [car, setCar] = useState(null);

  useEffect(() => {
    if (!carId) return;
    fetch(`${API}/noleggio/catalog?source=arval&limit=2000`)
      .then((r) => r.json())
      .then((d) => {
        const found = (d.cars || []).find((c) => c.id === `arval-${carId}` || c.id === carId);
        if (found) setCar(found);
      })
      .catch(() => {});
  }, [carId]);

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="max-w-3xl mx-auto px-5 sm:px-6 py-10" data-testid="pratica-arval-placeholder">
        <Link
          to="/noleggio"
          className="inline-flex items-center gap-1.5 text-sm text-[#4A5E51] hover:text-[#163321] mb-6"
          data-testid="back-to-noleggio"
        >
          <ArrowLeft className="w-4 h-4" /> Torna al catalogo
        </Link>

        <div className="bg-gradient-to-br from-[#D97B58]/15 to-[#163321]/10 border-2 border-dashed border-[#D97B58]/40 rounded-2xl p-8 sm:p-10 mb-6">
          <div className="flex items-start gap-4 mb-4">
            <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-[#D97B58]/20 flex items-center justify-center">
              <Construction className="w-6 h-6 text-[#D97B58]" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-[#D97B58] font-bold mb-1">
                In attivazione
              </p>
              <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#163321] leading-tight">
                Modulo pratica Arval in attivazione
              </h1>
            </div>
          </div>
          <p className="text-sm text-[#4A5E51] leading-relaxed">
            Il modulo di richiesta credito per le auto a marchio <strong>Arval</strong> sarà
            disponibile a breve. Stiamo finalizzando l'integrazione con Arval Italia per garantire
            un'esperienza di sottoscrizione digitale completa.
          </p>
          <p className="text-sm text-[#4A5E51] leading-relaxed mt-2">
            Nel frattempo, contatta direttamente i nostri uffici e un consulente Colucci ti
            seguirà personalmente nella raccolta documenti e nella sottoscrizione del contratto.
          </p>
        </div>

        {car && (
          <div className="bg-white rounded-xl border border-slate-200 p-5 mb-6" data-testid="pratica-arval-car-summary">
            <p className="text-[10px] uppercase tracking-widest text-[#4A5E51] mb-2">Auto selezionata</p>
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-[#163321]/10 flex items-center justify-center">
                <CarFront className="w-6 h-6 text-[#163321]" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="font-heading text-lg font-bold text-[#163321] leading-tight">
                  {car.brand} {car.model}
                </h2>
                <p className="text-xs text-[#4A5E51] mt-0.5">{car.modelDetails}</p>
                <p className="text-sm font-semibold text-[#163321] mt-2">
                  {car.monthlyFee ? `da ${car.monthlyFee}€/mese` : "Canone su richiesta"}
                  <span className="text-[#4A5E51] font-normal text-xs ml-1.5">· {car.durationMonths} mesi</span>
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl border border-slate-200 p-5 sm:p-6">
          <h3 className="font-heading text-base font-bold text-[#163321] mb-4">
            I nostri recapiti
          </h3>
          <div className="space-y-3 text-sm">
            <a href="tel:+390816805685" className="flex items-center gap-3 text-[#4A5E51] hover:text-[#163321]" data-testid="pratica-arval-phone">
              <div className="w-9 h-9 rounded-lg bg-[#163321]/10 flex items-center justify-center flex-shrink-0">
                <Phone className="w-4 h-4 text-[#163321]" />
              </div>
              <div>
                <p className="font-semibold text-[#163321]">081 761 1000</p>
                <p className="text-xs text-[#4A5E51]">Lun-Ven 9:00–18:00</p>
              </div>
            </a>
            <a href="mailto:info@colucciassicuratori.it?subject=Richiesta+info+pratica+Arval" className="flex items-center gap-3 text-[#4A5E51] hover:text-[#163321]" data-testid="pratica-arval-email">
              <div className="w-9 h-9 rounded-lg bg-[#163321]/10 flex items-center justify-center flex-shrink-0">
                <Mail className="w-4 h-4 text-[#163321]" />
              </div>
              <div>
                <p className="font-semibold text-[#163321]">info@colucciassicuratori.it</p>
                <p className="text-xs text-[#4A5E51]">Risposta entro 24h</p>
              </div>
            </a>
            <div className="flex items-start gap-3 text-[#4A5E51]">
              <div className="w-9 h-9 rounded-lg bg-[#163321]/10 flex items-center justify-center flex-shrink-0">
                <MapPin className="w-4 h-4 text-[#163321]" />
              </div>
              <div>
                <p className="font-semibold text-[#163321]">3 sedi in Campania</p>
                <p className="text-xs text-[#4A5E51]">Portici · Napoli · Pomigliano d'Arco</p>
              </div>
            </div>
          </div>
          <div className="mt-5 flex flex-col sm:flex-row gap-3">
            <Button asChild className="bg-[#163321] hover:bg-[#1F4030] text-white" data-testid="pratica-arval-cta-mail">
              <a href={`mailto:info@colucciassicuratori.it?subject=Richiesta+pratica+Arval${car ? `+${encodeURIComponent(car.brand + " " + car.model)}` : ""}&body=Vorrei+ricevere+info+sulla+pratica+NLT+Arval`}>
                Richiedi info su questa auto
              </a>
            </Button>
            <Button asChild variant="outline" data-testid="pratica-arval-back">
              <Link to="/noleggio?source=arval">Esplora altre auto Arval</Link>
            </Button>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
